import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { OrganizationBranding, BrandingUpdatePayload } from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

export function useOrganizationBranding() {
  return useQuery({
    queryKey: queryKeys.branding.current(),
    queryFn: async (): Promise<OrganizationBranding> => {
      const response = await apiClient.get<OrganizationBranding>('/api/v1/organizations/branding/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BrandingUpdatePayload): Promise<OrganizationBranding> => {
      const formData = new FormData();

      if (data.logo) {
        formData.append('logo', data.logo);
      }
      if (data.primary_color !== undefined) {
        formData.append('primary_color', data.primary_color);
      }
      if (data.secondary_color !== undefined) {
        formData.append('secondary_color', data.secondary_color);
      }
      if (data.address !== undefined) {
        formData.append('address', data.address);
      }
      if (data.phone !== undefined) {
        formData.append('phone', data.phone);
      }
      if (data.email !== undefined) {
        formData.append('email', data.email);
      }
      if (data.website !== undefined) {
        formData.append('website', data.website);
      }

      const response = await apiClient.patch<OrganizationBranding>(
        '/api/v1/organizations/branding/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.branding.all });
    },
  });
}

export function useRemoveLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete('/api/v1/organizations/branding/logo/');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.branding.all });
    },
  });
}
